// @ts-expect-error pure js
import { axiosPublic } from "../../api";
import { Form, Formik } from "formik";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import {
  signUpInitialValues,
  signUpProInitialValues,
  signUpSchema,
  type SignUpValues,
} from "../../models/signup";
// @ts-expect-error pure js
import { sanitizeValues } from "../../models/utils";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
// @ts-expect-error pure js
import { getPromoCode, getUtmParams, setUtmSearchParams } from "../../utils";
import PasswordInput from "../UI/PasswordInput";
import { PASSWORD_RULES_HINT } from "../../utils/constants/validation";
import { signupTrackingFields } from "../../utils/tracking";
import AuthHeader from "./AuthHeader";
import AuthFooter from "./AuthFooter";
import SignupSteps from "./SignupSteps";
import { NameFields, BailleurDetailsFields } from "./SignupBailleurFields";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import Callout from "../UI/Callout";
import styles from "./AuthFormSignup.module.css";

interface AuthFormSignupProps {
  /** Page d'inscription dédiée aux professionnels (agences / mandataires) :
   * pas de choix de profil ni de question de motivation, essai de 60 jours direct. */
  pro?: boolean;
}

export default function AuthFormSignup({ pro = false }: AuthFormSignupProps) {
  const nav = useNavigate();
  const [errMsg, setErrMsg] = useState("");
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const idParrain = searchParams.get("referral") || location.state?.referral;
  const utm = getUtmParams(searchParams) || location.state?.utm || {};
  const codePromo = getPromoCode(searchParams) || location.state?.promo;
  const loginUrl = new URL("/login", window.location.origin);
  if (idParrain) {
    loginUrl.searchParams.set("referral", idParrain);
  }
  if (codePromo) {
    loginUrl.searchParams.set("promo", codePromo);
  }
  if (utm) {
    setUtmSearchParams(loginUrl.searchParams, utm);
  }

  async function signMeUp(values: SignUpValues) {
    try {
      setErrMsg("");
      await axiosPublic.post("/signup", {
        id_parrain: idParrain,
        code_promo_inscription: codePromo,
        ...sanitizeValues(values),
        utm,
        ...signupTrackingFields(searchParams),
      });
      nav("/signup/verify", {
        replace: true,
        state: { from: location.state?.from, mail: values.mail },
      });
    } catch (err: unknown) {
      const error = err as { response?: { status: number } };
      if (!error?.response) {
        setErrMsg("Serveur de connexion injoignable");
      } else {
        setErrMsg("Erreur lors de la création de compte");
        console.error(error);
      }
    }
  }

  return (
    <Formik
      initialValues={pro ? signUpProInitialValues : signUpInitialValues}
      validationSchema={signUpSchema}
      onSubmit={signMeUp}
    >
      {({ values }) => {
        const fields = (
          <>
            <NameFields />
            <TextInput
              type="email"
              name="mail"
              label="Adresse e-mail"
              placeholder="votre@email.fr"
              marginForm=""
            />
            <PasswordInput
              name="password"
              label="Mot de passe"
              placeholder="Votre mot de passe"
              subText={PASSWORD_RULES_HINT}
              autoComplete="new-password"
              marginForm=""
            />
            <PasswordInput
              name="dPassword"
              label="Confirmer le mot de passe"
              placeholder="Confirmez votre mot de passe"
              autoComplete="new-password"
              marginForm=""
            />
            {(pro || values.home === "/bailleur") && <BailleurDetailsFields />}
          </>
        );

        return (
          <Form>
            <AuthHeader
              title={
                pro ? "Créer un compte professionnel" : "Créer un compte Qalimo"
              }
              subtitle={
                pro
                  ? "Agences et mandataires : remplissez le formulaire pour créer votre compte."
                  : "Veuillez remplir le formulaire pour créer votre compte."
              }
            />

            {pro ? (
              <div className={styles.inputs}>
                {fields}
                <Callout variant="danger">{errMsg}</Callout>
                <FormikSubmitButton className={styles.submitButton}>
                  Créer mon compte
                </FormikSubmitButton>
              </div>
            ) : (
              <SignupSteps id="signup" error={errMsg}>
                {fields}
              </SignupSteps>
            )}

            {!pro && (
              <AuthFooter
                linkText="Créer un compte agence ou un mandataire"
                to={{ pathname: "/signup/pro", search: location.search }}
                state={{
                  from: location.state?.from,
                  referral: idParrain,
                  promo: codePromo,
                  utm,
                }}
              />
            )}
            <AuthFooter
              label="Déjà un compte ?"
              linkText="Se connecter"
              to={loginUrl.pathname + loginUrl.search}
              state={{
                from: location.state?.from ?? location,
                referral: idParrain,
                promo: codePromo,
                utm,
              }}
            />
          </Form>
        );
      }}
    </Formik>
  );
}
